import { FormikProvider, Form, useFormik } from 'formik'
import { useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'

import {
   LocationDetailsType,
   LocationType,
   MarkupSettingsType,
   SaveLocationDetailsPayload,
} from 'shared/store/useLocations/types'
import { findCountryKeyByName } from 'shared/utils/helpers/findCountryName'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useDataLocationsMutation, useMarkupSettingsMutation } from 'shared/store/useLocations'
import useCountriesQuery from 'shared/store/useCountries'
import { UserRoles } from 'shared/consts'

import { FavouriteProducersResponse } from 'shared/store/useFavouriteProducers/types'
import { useFavouriteProducersMutation } from 'shared/store/useFavouriteProducers'
import { InfinityThresholdType, LocationAddressOptionType } from '../../types'
import AutoOrderingSettings from '../AutoOrderingSettings'
import LocationDetailsForm from '../LocationDetailsForm'
import { locationDetailsSchema } from '../../schema'
import { LocationTabContainer } from '../../styles'
import AdminDetailsForm from '../AdminDetailsForm'
import MarkupSettings from '../MarkupSettings'
import FavouriteProducers from '../FavouriteProducers'

interface Props {
   mainLocationUuid: string
   values?: LocationType
   openDeleteLocationModal: () => void
   roles: UserRoles[]
}

enum TabsEnum {
   LOCATION_DATA = 'LOCATION_DATA',
   ADMIN_DATA = 'ADMIN_DATA',
   CSV_CARTS = 'CSV_CARTS',
   MARKUP_SETTINGS = 'MARKUP_SETTINGS',
   FAVOURITE_PRODUCERS = 'FAVOURITE_PRODUCERS',
}
export default function LocationsSettings({ mainLocationUuid, roles, values, openDeleteLocationModal }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'LOCATIONS'])
   const [activeTab, setActiveTab] = useState<TabsEnum>(TabsEnum.LOCATION_DATA)
   const [infinityThresholdValues, setInfinityThresholdValues] = useState<InfinityThresholdType>({
      multiplier: 1,
      addend: 0,
   })
   const [activeCategory, setActiveCategory] = useState<string>('')
   const [disabledButton, setDisabledButton] = useState(false)
   const theme = useTheme()
   const [searchParams] = useSearchParams()
   const smallerScreen = useMediaQuery(theme.breakpoints.down('lg'))
   const { data: countriesData } = useCountriesQuery()
   const { mutate } = useDataLocationsMutation()
   const { mutate: saveFavouriteProducers, isLoading: isFavouriteProducersSaving } = useFavouriteProducersMutation()
   const { mutate: saveMarkup, isLoading: isSavingMarkup } = useMarkupSettingsMutation()
   const [options, setOptions] = useState<LocationAddressOptionType>({
      voivodeshipOptions: [],
      countyOptions: [],
   })
   const [selectedProducers, setSelectedProducers] = useState<{ [key: string]: string[] }>({
      PREMIUM: [],
      HIGH_QUALITY: [],
      MEDIUM_QUALITY: [],
      ECONOMY: [],
   })

   const formik = useFormik<LocationDetailsType>({
      initialValues: {
         locationSettings: {
            uuid: '',
            name: '',
            address: {
               street: '',
               zipCode: '',
               city: '',
               houseNumber: '',
               country: '',
               voivodeship: '',
               county: '',
            },
            shippingAddress: {
               country: '',
               street: '',
               zipCode: '',
               city: '',
               houseNumber: '',
            },
            orderReflinkSettings: {
               address: '',
               companyName: '',
               phoneNumber: '',
               defaultCartDescription: '',
            },
         },
         autoCartsSettings: {
            autoOrderPreferences: {
               preferredWholesalesUuid: [],
               priceConditionPercentage: null,
               priceConditionAmount: null,
               ignoreScoringRules: false,
               checkTotalAvailability: false,
               addPartialQuantityToCart: false,
            },
            locationUuid: null,
         },
         markupSettings: {
            priceType: 'NET',
            thresholds: [],
         },
      },
      validationSchema: locationDetailsSchema,
      onSubmit: handleSubmit,
   })

   const { setValues, setFieldValue, setFieldError, isValid } = formik

   function handleSubmit() {
      if (activeTab === TabsEnum.FAVOURITE_PRODUCERS) {
         const payload: FavouriteProducersResponse = {
            producers: {},
         }

         Object.entries(selectedProducers).forEach(([key, value]) => {
            value.forEach((item) => {
               payload.producers[item] = key
            })
         })
         saveFavouriteProducers({
            payload,
            locationUuid: values!.locationSettings.uuid,
            categoryUuid: activeCategory,
         })
      } else if (activeTab === TabsEnum.MARKUP_SETTINGS) {
         if (values) {
            const newMarkupSettings: MarkupSettingsType = {
               priceType: formik.values.markupSettings.priceType,
               thresholds: [
                  ...formik.values.markupSettings.thresholds,
                  {
                     ...infinityThresholdValues,
                     end: null,
                     start: Number(getLastThresholdEndValue()),
                  },
               ],
            }
            saveMarkup({ payload: newMarkupSettings, uuid: values.locationSettings.uuid })
         }
      } else if (!options.countyOptions.find((item) => item.value === formik.values.locationSettings.address.county)) {
         setFieldValue('locationSettings.address.county', null)
         setFieldError('locationSettings.address.county', 'required')
      } else if (values && countriesData) {
         const newSettings: SaveLocationDetailsPayload = {
            locationSettings: {
               address: {
                  city: formik.values.locationSettings.address.city,
                  zipCode: formik.values.locationSettings.address.zipCode,
                  street: formik.values.locationSettings.address.street,
                  houseNumber: formik.values.locationSettings.address.houseNumber,
                  country: findCountryKeyByName(
                     countriesData,
                     formik.values.locationSettings.address.country ?? 'Poland'
                  ),
                  county: formik.values.locationSettings.address.county,
                  voivodeship: formik.values.locationSettings.address.voivodeship,
               },
               shippingAddress: {
                  country: formik.values.locationSettings.shippingAddress.country ?? 'Poland',
                  city: formik.values.locationSettings.shippingAddress.city,
                  zipCode: formik.values.locationSettings.shippingAddress.zipCode,
                  street: formik.values.locationSettings.shippingAddress.street,
                  houseNumber: formik.values.locationSettings.shippingAddress.houseNumber,
               },
               orderReflinkSettings: {
                  address: formik.values.locationSettings.orderReflinkSettings?.address,
                  companyName: formik.values.locationSettings.orderReflinkSettings?.companyName,
                  phoneNumber: formik.values.locationSettings.orderReflinkSettings?.phoneNumber,
                  defaultCartDescription: formik.values.locationSettings.orderReflinkSettings?.defaultCartDescription,
               },
               locationName: formik.values.locationSettings.name,
               uuid: formik.values.locationSettings.uuid,
            },
            autoCartsSettings:
               roles.includes(UserRoles.PRO) ||
               roles.includes(UserRoles.ULTRA) ||
               roles.includes(UserRoles.NITRO) ||
               roles.includes(UserRoles.TURBO)
                  ? formik.values.autoCartsSettings
                  : null,
            markupSettings: {
               priceType: formik.values.markupSettings.priceType,
               thresholds: [
                  ...formik.values.markupSettings.thresholds,
                  {
                     ...infinityThresholdValues,
                     end: null,
                     start: Number(getLastThresholdEndValue()),
                  },
               ],
            },
         }
         mutate({ payload: newSettings, uuid: values.locationSettings.uuid })
      }
   }

   function getLastThresholdEndValue() {
      const lastElement = formik.values.markupSettings.thresholds[formik.values.markupSettings.thresholds.length - 1]
      if (lastElement) {
         const { end } = lastElement
         if (end) {
            return (end + 0.01).toFixed(2)
         }
         return 0
      }
      return 0
   }

   function renderTabContentForBasic() {
      if (values) {
         switch (activeTab) {
            case TabsEnum.FAVOURITE_PRODUCERS: {
               return (
                  <FavouriteProducers
                     locationUuid={values.locationSettings.uuid}
                     activeCategory={activeCategory}
                     setActiveCategory={setActiveCategory}
                     selectedProducers={selectedProducers}
                     setSelectedProducers={setSelectedProducers}
                  />
               )
            }
            case TabsEnum.LOCATION_DATA:
               return (
                  <LocationDetailsForm
                     roles={roles}
                     openDeleteLocationModal={() => openDeleteLocationModal()}
                     mainLocationUuid={mainLocationUuid}
                     options={options}
                  />
               )
            case TabsEnum.MARKUP_SETTINGS: {
               return (
                  <MarkupSettings
                     infinityThreshold={infinityThresholdValues}
                     setInfinityThreshold={(infinityValues: InfinityThresholdType) =>
                        setInfinityThresholdValues(infinityValues)
                     }
                  />
               )
            }
            default:
               return null
         }
      }
      return null
   }

   function renderSaveButton() {
      if (
         roles.includes(UserRoles.PRO) ||
         roles.includes(UserRoles.ULTRA) ||
         roles.includes(UserRoles.TURBO) ||
         roles.includes(UserRoles.NITRO)
      ) {
         if (activeTab !== TabsEnum.ADMIN_DATA) {
            return (
               <ContainedButton
                  disabled={
                     (activeTab !== TabsEnum.MARKUP_SETTINGS &&
                        (!isValid || disabledButton || values?.locationSettings.disabled)) ||
                     (activeTab === TabsEnum.MARKUP_SETTINGS &&
                        ((formik.values.markupSettings.thresholds.length > 0 &&
                           !formik.values.markupSettings.thresholds[formik.values.markupSettings.thresholds.length - 1]
                              .end) ||
                           formik.values.markupSettings.thresholds.length === 20 ||
                           (formik.values.markupSettings.thresholds.length > 0 &&
                              !!formik.values.markupSettings.thresholds.find(
                                 (item) => item.end && item.start > item.end
                              )) ||
                           (formik.values.markupSettings.thresholds.length > 0 &&
                              !!formik.values.markupSettings.thresholds.find((item) => item.multiplier < 1))))
                  }
                  sx={{ alignSelf: 'center' }}
                  variant="contained"
                  onClick={handleSubmit}
               >
                  {t('TRANSLATION:save')}
               </ContainedButton>
            )
         }
         return null
      }
      return (
         <ContainedButton
            disabled={
               (activeTab !== TabsEnum.MARKUP_SETTINGS &&
                  (!isValid || disabledButton || values?.locationSettings.disabled)) ||
               (activeTab === TabsEnum.MARKUP_SETTINGS &&
                  ((formik.values.markupSettings.thresholds.length > 0 &&
                     !formik.values.markupSettings.thresholds[formik.values.markupSettings.thresholds.length - 1]
                        .end) ||
                     formik.values.markupSettings.thresholds.length === 20 ||
                     (formik.values.markupSettings.thresholds.length > 0 &&
                        !!formik.values.markupSettings.thresholds.find((item) => item.end && item.start > item.end)) ||
                     (formik.values.markupSettings.thresholds.length > 0 &&
                        !!formik.values.markupSettings.thresholds.find((item) => item.multiplier < 1))))
            }
            sx={{ alignSelf: 'center' }}
            variant="contained"
            onClick={handleSubmit}
         >
            {t('TRANSLATION:save')}
         </ContainedButton>
      )
   }

   function renderTabContent() {
      if (values) {
         switch (activeTab) {
            case TabsEnum.FAVOURITE_PRODUCERS: {
               return (
                  <FavouriteProducers
                     locationUuid={values.locationSettings.uuid}
                     activeCategory={activeCategory}
                     setActiveCategory={setActiveCategory}
                     selectedProducers={selectedProducers}
                     setSelectedProducers={setSelectedProducers}
                  />
               )
            }
            case TabsEnum.LOCATION_DATA:
               return (
                  <LocationDetailsForm
                     roles={roles}
                     openDeleteLocationModal={() => openDeleteLocationModal()}
                     mainLocationUuid={mainLocationUuid}
                     options={options}
                  />
               )
            case TabsEnum.ADMIN_DATA: {
               return (
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        py: 2,
                        px: { md: '25%', xs: 0 },
                        minHeight: 400,
                     }}
                  >
                     {roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) && (
                        <AdminDetailsForm mainLocationUuid={mainLocationUuid} values={values} />
                     )}
                  </Box>
               )
            }
            case TabsEnum.CSV_CARTS: {
               return <AutoOrderingSettings locationUuid={values.locationSettings.uuid} />
            }
            case TabsEnum.MARKUP_SETTINGS: {
               return (
                  <MarkupSettings
                     infinityThreshold={infinityThresholdValues}
                     setInfinityThreshold={(infinityValues: InfinityThresholdType) =>
                        setInfinityThresholdValues(infinityValues)
                     }
                  />
               )
            }
            default:
               return null
         }
      }
      return null
   }

   useEffect(() => {
      const isRedirect = searchParams.get('clientCarts') === 'true'
      if (
         isRedirect &&
         (roles.includes(UserRoles.PRO) ||
            roles.includes(UserRoles.ULTRA) ||
            roles.includes(UserRoles.TURBO) ||
            roles.includes(UserRoles.NITRO))
      ) {
         setActiveTab(TabsEnum.CSV_CARTS)
      }
   }, [])

   useEffect(() => {}, [formik.values])

   useEffect(() => {
      setActiveCategory('')
   }, [activeTab])

   useEffect(() => {
      if (values) {
         const { thresholds } = formik.values.markupSettings
         if (
            (thresholds.length > 0 && !thresholds[thresholds.length - 1].end) ||
            (thresholds.length > 0 && thresholds.find((item) => item.end && item.start > item.end)) ||
            (thresholds.length > 0 && thresholds.find((item) => item.multiplier < 1))
         ) {
            setDisabledButton(true)
         } else {
            setDisabledButton(false)
         }
      } else {
         setDisabledButton(false)
      }
   }, [formik.values])

   useEffect(() => {
      if (countriesData && formik.values && values && formik.values.locationSettings.address.country === 'Polska') {
         const countryKey = findCountryKeyByName(countriesData, formik.values.locationSettings.address.country)
         if (countryKey) {
            const selectedCountry = countriesData[countryKey]
            if (selectedCountry) {
               setOptions({
                  voivodeshipOptions: Object.keys(selectedCountry.voivodeships).map((item) => ({
                     name: item,
                     value: item,
                  })),
                  countyOptions: formik.values.locationSettings.address.voivodeship
                     ? selectedCountry.voivodeships[formik.values.locationSettings.address.voivodeship].map((item) => ({
                          name: item,
                          value: item,
                       }))
                     : [],
               })
            }
         }
      }
   }, [countriesData, formik.values, values])

   useEffect(() => {
      if (values && countriesData) {
         setValues({
            ...values,
            locationSettings: {
               ...values.locationSettings,
               address: {
                  ...values.locationSettings.address,
                  country: countriesData[values.locationSettings.address.country].name,
               },
               orderReflinkSettings: {
                  companyName: values.locationSettings.orderReflinkSettings?.companyName || '',
                  address: values.locationSettings.orderReflinkSettings?.address || '',
                  phoneNumber: values.locationSettings.orderReflinkSettings?.phoneNumber || '',
                  defaultCartDescription: values.locationSettings.orderReflinkSettings?.defaultCartDescription || '',
               },
            },
            autoCartsSettings: {
               ...values.autoCartsSettings,
               autoOrderPreferences: {
                  ...values.autoCartsSettings.autoOrderPreferences,
                  addPartialQuantityToCart:
                     values.autoCartsSettings.autoOrderPreferences.addPartialQuantityToCart || false,
                  checkTotalAvailability: values.autoCartsSettings.autoOrderPreferences.checkTotalAvailability || false,
                  ignoreScoringRules: values.autoCartsSettings.autoOrderPreferences.ignoreScoringRules || false,
                  preferredWholesalesUuid: values.autoCartsSettings.autoOrderPreferences.preferredWholesalesUuid || [],
                  priceConditionAmount: values.autoCartsSettings.autoOrderPreferences.priceConditionAmount || 0,
                  priceConditionPercentage: values.autoCartsSettings.autoOrderPreferences.priceConditionPercentage || 0,
               },
               locationUuid: values.autoCartsSettings.locationUuid || null,
            },
            markupSettings: {
               priceType: values.markupSettings.priceType || 'NET',
               thresholds: values.markupSettings.thresholds ? values.markupSettings.thresholds.slice(0, -1) : [],
            },
         })
         if (values.markupSettings.thresholds && values.markupSettings.thresholds.length > 0) {
            setInfinityThresholdValues({
               multiplier: values.markupSettings.thresholds[values.markupSettings.thresholds.length - 1].multiplier,
               addend: values.markupSettings.thresholds[values.markupSettings.thresholds.length - 1].addend,
            })
         }
      }
   }, [countriesData, values])

   return (
      <Grid size={{ xs: 12, md: 10, lg: 10 }}>
         <Box sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
            <FormikProvider value={formik}>
               {formik.values && options && (
                  <Form>
                     <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                           variant={smallerScreen ? 'scrollable' : 'standard'}
                           centered
                           sx={{ display: 'flex', justifyContent: 'center' }}
                           value={activeTab}
                           onChange={(e, value) => setActiveTab(value)}
                        >
                           <Tab
                              sx={{ fontSize: 12 }}
                              value={TabsEnum.LOCATION_DATA}
                              label={t('LOCATIONS:tabsLabels.locationData')}
                           />
                           {(roles.includes(UserRoles.ULTRA) || roles.includes(UserRoles.NITRO)) &&
                              roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN) && (
                                 <Tab
                                    sx={{ fontSize: 12 }}
                                    value={TabsEnum.ADMIN_DATA}
                                    label={t('LOCATIONS:tabsLabels.adminData')}
                                 />
                              )}
                           {(roles.includes(UserRoles.PRO) ||
                              roles.includes(UserRoles.ULTRA) ||
                              roles.includes(UserRoles.TURBO) ||
                              roles.includes(UserRoles.NITRO)) && (
                              <Tab
                                 sx={{ fontSize: 12 }}
                                 value={TabsEnum.CSV_CARTS}
                                 label={t('LOCATIONS:tabsLabels.csvCartsSettings')}
                              />
                           )}
                           <Tab
                              sx={{ fontSize: 12 }}
                              value={TabsEnum.MARKUP_SETTINGS}
                              label={t('LOCATIONS:tabsLabels.markupSettings')}
                           />
                           {(roles.includes(UserRoles.LOCALIZATION_ADMIN) ||
                              roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN)) &&
                              !roles.includes(UserRoles.DEMO) &&
                              !roles.includes(UserRoles.DEMO_SUBSCRIPTION) && (
                                 <Tab
                                    sx={{ fontSize: 12 }}
                                    value={TabsEnum.FAVOURITE_PRODUCERS}
                                    label={t('LOCATIONS:tabsLabels.favouriteProducersSettings')}
                                 />
                              )}
                        </Tabs>
                     </Box>
                     <LocationTabContainer>
                        {roles.includes(UserRoles.PRO) ||
                        roles.includes(UserRoles.ULTRA) ||
                        roles.includes(UserRoles.TURBO) ||
                        roles.includes(UserRoles.NITRO)
                           ? renderTabContent()
                           : renderTabContentForBasic()}
                        {renderSaveButton()}
                     </LocationTabContainer>
                  </Form>
               )}
            </FormikProvider>
         </Box>
      </Grid>
   )
}
